import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { ensureMsalInitialized, msalInstance } from "./auth/msal.config";

import BudgetEditor from "./components/BudgetEditor";
import LoginView from "./components/LoginView";
import ChangePasswordView from "./components/ChangePasswordView";
import ResetPasswordView from "./components/ResetPasswordView";
import EntraCallback from "./components/EntraCallback";
import EntraLoginButton from "./components/EntraLoginButton";
import HeadcountEditor from "./components/HeadcountEditor";
import Dashboard from "./components/Dashboard";
import AdminPanel from "./components/AdminPanel";
import SalaryView from "./components/SalaryView";
import TempLaborView from "./components/TempLaborView";
import BudgetCompareView from "./components/BudgetCompareView";
import BudgetOverviewView from "./components/BudgetOverviewView";
import YTGSimulationStrip from "./components/YTGSimulationStrip";
// import TeamView from "./components/TeamView";  // replaced by ActualsDashboard
import ActualsDashboard from "./components/ActualsDashboard";

import { AuthenticatedUser, CostCenterInfo, EntraInfo, api, getEntraInfo } from "./api";
import { trackEvent, trackPageView } from "./telemetry";
import "./components/BudgetEditor.css";
import "./App.css";

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inPopupWindow = !!window.opener && window.opener !== window;
  const [popupAuthPending, setPopupAuthPending] = useState(inPopupWindow);
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    const saved = localStorage.getItem("authUser");
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (!inPopupWindow) return;

    const completePopupAuth = async () => {
      try {
        await ensureMsalInitialized(msalInstance);
        await msalInstance.handleRedirectPromise();
      } catch {
        // Ignore popup callback errors and return control to opener.
      } finally {
        setPopupAuthPending(false);
        window.close();
      }
    };

    void completePopupAuth();
  }, [inPopupWindow]);

  const isAdmin = authenticatedUser?.role === "admin" || authenticatedUser?.role === "super_admin";
  const isSuperAdmin = authenticatedUser?.role === "super_admin";
  const hasLeadershipView = authenticatedUser?.role === "cc_leader" || authenticatedUser?.role === "bu_leader" || authenticatedUser?.role === "vp";

  // Active fiscal year from settings API
  const [activeFY, setActiveFY] = useState<string | null>(null);

  // Forecast window status for header note
  const [forecastWindowInfo, setForecastWindowInfo] = useState<{
    windowOpen: boolean;
    activeDim: string | null;
    closeDate: string | null;
    nextWindowDim: string | null;
    nextWindowDate: string | null;
  }>({ windowOpen: false, activeDim: null, closeDate: null, nextWindowDim: null, nextWindowDate: null });

  // Help URL for header button
  const [helpUrl, setHelpUrl] = useState<string>("");
  const [entraInfo, setEntraInfo] = useState<EntraInfo | null>(null);
  const [entraInfoLoaded, setEntraInfoLoaded] = useState(false);
  const [entraPromptMessage, setEntraPromptMessage] = useState("");

  // Multi-CC support: track which CC is currently selected
  // Filter out CCs deactivated for the current FY or earlier
  const allUserCCs = authenticatedUser?.cost_centers || [];
  const fyForFilter = activeFY || (() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `FY${(yr % 100).toString().padStart(2, '0')}`;
  })();
  const userCCs = allUserCCs.filter(cc => {
    if (!cc.deactivated_from_fy) return true;
    return cc.deactivated_from_fy > fyForFilter;
  });
  const [selectedCCNum, setSelectedCCNum] = useState<string | null>(() => {
    const saved = localStorage.getItem("selectedCC");
    return saved || null;
  });
  // Forecast Comparison / Budget Overview only — "team" rolls up every CC
  // the user leads instead of showing one CC. Lives alongside the CC pills
  // (not inside each view) so picking a CC always clears team mode and
  // vice versa, instead of two independent, conflictable toggles.
  const [forecastScope, setForecastScope] = useState<"cc" | "team">("cc");
  // Resolve the selected CC (fall back to owned_cost_center or first in list)
  const selectedCC: CostCenterInfo | null = (() => {
    if (userCCs.length === 0) return null;
    return userCCs.find(c => c.cost_center_number === selectedCCNum)
      || userCCs.find(c => c.cost_center_number === authenticatedUser?.owned_cost_center)
      || userCCs[0];
  })();
  const hasOwnCC = !!selectedCC;

  // ── Admin impersonation — view & act as a CC owner ──────────────────────
  // State is intentionally NOT persisted to localStorage (clears on refresh)
  const [simRefreshKey, setSimRefreshKey] = useState(0);
  const bumpSimulation = () => setSimRefreshKey(k => k + 1);

  const [impersonatingCC, setImpersonatingCC] = useState<{
    cost_center_number: string;
    cost_center_name: string;
    cost_center_owner: string;
  } | null>(null);

  const isImpersonating = isAdmin && impersonatingCC !== null;
  const effectiveIsAdmin = isAdmin && !isImpersonating;

  const effectiveCC: CostCenterInfo | null = isImpersonating
    ? { ...impersonatingCC!, deactivated_from_fy: null }
    : selectedCC;
  const effectiveHasOwnCC = isImpersonating ? true : hasOwnCC;

  // Compute FY label and range from activeFY (e.g. "FY25" → April 2025 – March 2026)
  const fyLabel = activeFY || (() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `FY${(yr % 100).toString().padStart(2, '0')}`;
  })();
  const fyNum = parseInt(fyLabel.replace(/\D/g, ''), 10);
  const fyStartYear = 2000 + fyNum;
  const fyEndYear = fyStartYear + 1;
  const fyRange = `April ${fyStartYear} – March ${fyEndYear}`;

  // When the CC owner's active forecast window is a Planning Round (which
  // carries its own FY, e.g. "FY27 Planning Round 1"), the header badge
  // should reflect that FY instead of always showing the static global
  // toggle — otherwise a CC owner actively working FY27 planning still
  // sees "FY26" with no indication they're in the next year's cycle.
  const activeRoundFYMatch = forecastWindowInfo.activeDim?.match(/^(FY\d+)\s+Planning Round/i);
  const headerFY = activeRoundFYMatch ? activeRoundFYMatch[1].toUpperCase() : fyLabel;
  const headerFYNum = parseInt(headerFY.replace(/\D/g, ''), 10);
  const headerFYRange = `April ${2000 + headerFYNum} – March ${2001 + headerFYNum}`;

  // ── Derive navigation state from URL ──────────────────────────────────────
  const pathSegment = location.pathname.split("/")[1] || "";

  useEffect(() => {
    trackPageView(document.title, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Admin sub-tabs
  const ADMIN_SETUP_URLS = new Set(["business-units", "cost-centers", "users", "accounts", "data-upload", "salary-config"]);
  const adminSubTab = location.pathname.split("/")[2] || "";

  // Primary tab: actuals | forecast | admin-forecast | admin-setup
  const FORECAST_URLS = new Set(["expense", "salary", "headcount", "temp-labor", "forecast-comparison", "budget-overview"]);
  const primaryTab = pathSegment === "admin" && ADMIN_SETUP_URLS.has(adminSubTab) ? "admin-setup"
    : pathSegment === "admin" ? "admin-forecast"
    : FORECAST_URLS.has(pathSegment) ? "forecast"
    : "actuals";

  // Sub-tab: the path segment itself
  const subTab = pathSegment || "dashboard";

  const isEntraLinked = !!(entraInfo?.entra_user_id || entraInfo?.is_entra_linked);
  const isSsoSession = (entraInfo?.auth_method || "").toLowerCase() === "entra" || (entraInfo?.auth_method || "").toLowerCase() === "sso";
  const shouldOfferEntraLink = !!authenticatedUser && entraInfoLoaded && !isEntraLinked;

  const ssoButtonLabel = !entraInfoLoaded
    ? "Login with Daikin SSO"
    : isSsoSession && isEntraLinked
      ? "Daikin SSO"
      : isEntraLinked
        ? "Login with Daikin SSO"
        : "Link Daikin ID";

  // Auth is initialized from localStorage synchronously in useState above

  // Fetch active fiscal year from settings (available to all authenticated users)
  useEffect(() => {
    if (authenticatedUser) {
      api.get("/settings/fiscal-year")
        .then(r => { if (r.data.fiscalYear) setActiveFY(r.data.fiscalYear); })
        .catch(() => {});
      // Fetch role-appropriate help URL
      api.get("/settings/help-url")
        .then(r => {
          setHelpUrl(effectiveIsAdmin ? (r.data.help_url_admin || "") : (r.data.help_url_user || ""));
        })
        .catch(() => {});
    }
  }, [authenticatedUser, effectiveIsAdmin]);

  useEffect(() => {
    if (!authenticatedUser) {
      setEntraInfo(null);
      setEntraInfoLoaded(false);
      setEntraPromptMessage("");
      return;
    }

    let cancelled = false;

    const loadEntraInfo = async () => {
      try {
        const res = await getEntraInfo();
        if (cancelled) return;

        setEntraInfo(res.data);
        setEntraInfoLoaded(true);
      } catch {
        if (!cancelled) {
          setEntraInfoLoaded(true);
        }
      }
    };

    void loadEntraInfo();

    return () => {
      cancelled = true;
    };
  }, [authenticatedUser]);

  // Fetch forecast window config — determines if a window is currently open.
  // /settings/forecast-config is the source of truth for "is something open
  // right now" — it already knows about Planning Round dimensions (which carry
  // their own fiscal year, e.g. "FY27 Planning Round 1", independent of the
  // active-FY toggle), so its `active`/`dimension`/`close_date` are used
  // directly rather than re-derived against a toggle-FY-scoped window list.
  useEffect(() => {
    if (!authenticatedUser || effectiveIsAdmin) return;
    const fetchWindowInfo = async () => {
      try {
        const [configRes, windowsRes] = await Promise.all([
          api.get("/settings/forecast-config"),
          api.get("/settings/dimension-windows"), // no fy filter — may include a Planning Round's own window
        ]);
        const isWindowOpen = configRes.data.active as boolean;
        const adminDim = (configRes.data.dimension || null) as string | null;
        const closeDate = configRes.data.close_date
          ? new Date(configRes.data.close_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : null;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const windows = (windowsRes.data || []) as { dimension: string; open_from: string; open_to: string }[];

        // Find the next future window (open_from is after today) across any FY
        const futureWindows = windows
          .filter(w => new Date(w.open_from) > now)
          .sort((a, b) => new Date(a.open_from).getTime() - new Date(b.open_from).getTime());
        const next = futureWindows[0] || null;

        setForecastWindowInfo({
          windowOpen: isWindowOpen,
          activeDim: isWindowOpen ? adminDim : null,
          closeDate,
          nextWindowDim: next?.dimension?.replace(" Forecast", "") || null,
          nextWindowDate: next ? new Date(next.open_from).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null,
        });
      } catch { /* silent */ }
    };
    fetchWindowInfo();
  }, [authenticatedUser, effectiveIsAdmin, activeFY]);

  // Listen for real-time FY changes from AdminPanel
  useEffect(() => {
    const handler = (e: Event) => setActiveFY((e as CustomEvent).detail);
    window.addEventListener("fy-changed", handler);
    return () => window.removeEventListener("fy-changed", handler);
  }, []);

  useEffect(() => {
    if (authenticatedUser) {
      if (authenticatedUser.is_first_login && location.pathname !== "/change-password") {
        navigate("/change-password", {
          state: { email: authenticatedUser.email },
        });
      } else if (location.pathname === "/login") {
        if (isAdmin && !isImpersonating) navigate("/admin/bu-dashboard");
        else if (hasOwnCC) navigate("/dashboard");
        else if (hasLeadershipView) navigate("/team");
        else navigate("/dashboard");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser, navigate, location.pathname]);

  const handleLoginSuccess = (user: AuthenticatedUser, token: string) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(user));
    setAuthenticatedUser(user);
    setEntraPromptMessage("");
    trackEvent("frontend_login_success", {
      role: user.role,
      firstLogin: String(user.is_first_login),
    });
  };

  const handleSelectCC = (ccNum: string) => {
    setSelectedCCNum(ccNum);
    localStorage.setItem("selectedCC", ccNum);
    setForecastScope("cc");
    // Picking a CC while on the Actuals Team rollup implicitly means "show
    // me this one" — navigate back to the per-CC view instead of leaving
    // the user on /team wondering why nothing changed. Forecast's CC pills
    // never hit this branch since "team" isn't a Forecast subTab.
    if (subTab === "team") navigate("/dashboard");
  };

  const handleLogout = () => {
    if (authenticatedUser) {
      trackEvent("frontend_logout", { role: authenticatedUser.role });
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("selectedCC");
    setAuthenticatedUser(null);
    setEntraInfo(null);
    setEntraInfoLoaded(false);
    setEntraPromptMessage("");
    navigate("/login");
  };

  const handleEntraSsoError = (message: string) => {
    setEntraPromptMessage(message);
  };

  // ── Impersonation handlers ───────────────────────────────────────────────
  // Ref prevents auto-exit from firing during the start transition
  // (location.pathname is still "/admin/..." when impersonation state first applies)
  const impersonationStartingRef = useRef(false);

  const handleStartImpersonation = (ccNum: string, ccName: string, ccOwner: string) => {
    impersonationStartingRef.current = true;
    setImpersonatingCC({ cost_center_number: ccNum, cost_center_name: ccName, cost_center_owner: ccOwner });
    navigate("/dashboard");
  };

  const handleExitImpersonation = () => {
    setImpersonatingCC(null);
    navigate("/admin/bu-dashboard");
  };

  // Auto-exit impersonation if browser-back navigates to an admin route
  useEffect(() => {
    if (impersonationStartingRef.current) {
      impersonationStartingRef.current = false;
      return;
    }
    if (isImpersonating && location.pathname.startsWith("/admin")) {
      setImpersonatingCC(null);
    }
  }, [isImpersonating, location.pathname]);

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!authenticatedUser) return <Navigate to="/login" replace />;
    return <>{children}</>;
  };

  // Header role label
  const roleLabel = isImpersonating ? "Admin · Viewing as CC Owner"
    : isSuperAdmin ? "Super Admin"
    : isAdmin ? "Admin"
    : authenticatedUser?.role === "vp" ? "VP"
    : authenticatedUser?.role === "bu_leader" ? "BU Leader"
    : authenticatedUser?.role === "cc_leader" ? "CC Leader"
    : "";

  // Whether scope toggle row should show (Actuals mode, both CC and team available)
  const showScopeRow = !effectiveIsAdmin && !isImpersonating && primaryTab === "actuals" && hasOwnCC && hasLeadershipView;

  // Combined row: the Actuals My Team toggle and the CC pills live in the
  // same row now (previously the toggle floated alone in the header,
  // disconnected from the CC picker below it). Needs its own condition
  // since showScopeRow can be true with only 1 owned CC (no CC pills at
  // all). CC pills stay visible on /team too (no subTab exclusion) so the
  // row reads as one persistent control, not something that partially
  // vanishes depending on which view you're in.
  const showCCRow = !effectiveIsAdmin && !isImpersonating && (showScopeRow || userCCs.length > 1);

  const AppLayout = () => (
    <div>
      <div className="app-header">
        {/* Row 1 left: title + meta */}
        <div className="app-header-left">
          <span className="app-header-title">
            QFT {roleLabel && `· ${roleLabel}`}
            <span className={`fy-badge${activeRoundFYMatch ? " fy-badge--planning" : ""}`}>{headerFY}</span>
            {activeRoundFYMatch && <span className="fy-badge-planning-tag">Planning</span>}
            <span className="fy-range">({headerFYRange})</span>
          </span>
          {authenticatedUser && (
            <span className="app-header-meta">
              <span className="app-header-meta-label">User</span>
              {authenticatedUser.name}
              {effectiveHasOwnCC && (isImpersonating || userCCs.length <= 1) && (
                <>
                  <span className="app-header-meta-sep">·</span>
                  <span className="app-header-meta-label">CC</span>
                  {effectiveCC!.cost_center_number} – {effectiveCC!.cost_center_name}
                </>
              )}
            </span>
          )}
        </div>

        {/* Budgeting Window status (forecast pages) */}
        {!effectiveIsAdmin && primaryTab === "forecast" && (
          <div className={`forecast-window-note ${forecastWindowInfo.windowOpen ? "forecast-window-note--open" : "forecast-window-note--closed"}`}>
            <div className="fw-row">
              <span className="fw-label">Budgeting Window</span>
              {forecastWindowInfo.windowOpen ? (
                <span className="fw-status fw-status--open">Open</span>
              ) : (
                <span className="fw-status fw-status--closed">Closed</span>
              )}
            </div>
            <div className="fw-row-detail">
              {forecastWindowInfo.windowOpen ? (
                <span className="fw-detail">
                  {/Planning Round \d+$/i.test(forecastWindowInfo.activeDim || "")
                    ? forecastWindowInfo.activeDim
                    : `${forecastWindowInfo.activeDim?.replace(" Forecast", "")} Forecast`} closes <strong>{forecastWindowInfo.closeDate}</strong>
                </span>
              ) : forecastWindowInfo.nextWindowDate && forecastWindowInfo.nextWindowDim ? (
                <span className="fw-detail">
                  {/Planning Round \d+$/i.test(forecastWindowInfo.nextWindowDim)
                    ? forecastWindowInfo.nextWindowDim
                    : `${forecastWindowInfo.nextWindowDim} Forecast`} opens <strong>{forecastWindowInfo.nextWindowDate}</strong>
                </span>
              ) : (
                <span className="fw-detail">No upcoming windows scheduled</span>
              )}
            </div>
          </div>
        )}

        {/* Row 1 center: primary nav tabs */}
        {!effectiveIsAdmin && (
          <div className="app-header-nav" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="nav-tabs">
              <button
                onClick={() => navigate(effectiveHasOwnCC ? "/dashboard" : "/team")}
                className={`nav-tab${primaryTab === "actuals" ? " nav-tab--active" : ""}`}
              >
                <span className="material-icons">bar_chart</span>
                Actuals
              </button>
              {effectiveHasOwnCC && (
                <button
                  onClick={() => navigate("/expense")}
                  className={`nav-tab${primaryTab === "forecast" ? " nav-tab--active" : ""}`}
                >
                  <span className="material-icons">trending_up</span>
                  Forecast
                </button>
              )}
            </div>
          </div>
        )}

        {effectiveIsAdmin && (
          <div className="app-header-nav" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="nav-tabs">
              <button
                onClick={() => navigate("/admin/bu-dashboard")}
                className={`nav-tab${primaryTab === "admin-forecast" ? " nav-tab--active" : ""}`}
              >
                <span className="material-icons">trending_up</span>
                Forecast Setup
              </button>
              <button
                onClick={() => navigate(isSuperAdmin ? "/admin/business-units" : "/admin/cost-centers")}
                className={`nav-tab nav-tab--admin-setup${primaryTab === "admin-setup" ? " nav-tab--active" : ""}`}
              >
                <span className="material-icons">admin_panel_settings</span>
                Admin Setup
              </button>
            </div>
          </div>
        )}

        {/* Row 1 right: help + logout */}
        <div className="app-header-actions">
          <div id="header-notes-slot" />
          {authenticatedUser && (
            <EntraLoginButton
              className="header-sso-btn"
              label={ssoButtonLabel}
              showSuccess={isSsoSession && isEntraLinked}
              disabled={isSsoSession && isEntraLinked}
              onError={handleEntraSsoError}
              onSuccess={(user, token) => {
                setEntraPromptMessage("");
                handleLoginSuccess(user, token);
              }}
            />
          )}

          <button
            onClick={() => {
              if (helpUrl) window.open(helpUrl, "_blank", "noopener");
              else alert(effectiveIsAdmin ? "Configure help URLs in Forecast Setup > Forecast Windows." : "Help page not configured yet. Contact your admin.");
            }}
            className="help-btn help-btn--icon-only"
            title="Help"
            aria-label="Help"
          >
            <span className="material-icons">help_outline</span>
          </button>
          <button onClick={handleLogout} className="logout-btn logout-btn--icon-only" title="Logout" aria-label="Logout">
            <span className="material-icons">logout</span>
          </button>
        </div>

      </div>

      {!!entraPromptMessage && (
        <div className="entra-link-message" role="alert">
          {entraPromptMessage}
        </div>
      )}

      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="impersonation-banner">
          <span className="material-icons">visibility</span>
          <span className="impersonation-banner-text">
            Viewing as <strong>{impersonatingCC!.cost_center_number} – {impersonatingCC!.cost_center_name}</strong>
            {impersonatingCC!.cost_center_owner && (
              <span className="impersonation-banner-owner"> (Owner: {impersonatingCC!.cost_center_owner})</span>
            )}
          </span>
          <button className="impersonation-banner-exit" onClick={handleExitImpersonation}>
            <span className="material-icons">close</span>
            Exit View
          </button>
        </div>
      )}

      {/* CC selector row — CC pills first, "My Team" after them with a
          separator (same order as Forecast's version, which this now
          matches exactly instead of leading with a redundant "My Cost
          Centers" button — clicking any CC pill already means "show me my
          cost centers"). Row stays visible on /team so "My Team" doesn't
          disappear along with its own active state once you're on it —
          picking a CC pill from there navigates back to /dashboard (see
          handleSelectCC). On Actuals, this row also hosts the FY/Month/
          Action Plan controls (right-aligned) — it always renders there
          even with no CC pills to show, since a single-CC, non-leader user
          still needs a home for those controls. */}
      {(showCCRow || (!effectiveIsAdmin && !isImpersonating && primaryTab === "actuals")) && (
        <div className={`cc-content-row${userCCs.length <= 1 && showScopeRow ? " cc-content-row--no-label" : ""}`}>
          {showCCRow && (
            <div className="cc-selector">
              {userCCs.length > 1 && userCCs.map(cc => (
                <button
                  key={cc.cost_center_number}
                  className={`cc-pill${subTab !== "team" && cc.cost_center_number === selectedCC?.cost_center_number && forecastScope === "cc" ? " cc-pill--active" : ""}`}
                  onClick={() => handleSelectCC(cc.cost_center_number)}
                  title={cc.cost_center_name}
                >
                  {cc.cost_center_number} – {cc.cost_center_name}
                </button>
              ))}
              {showScopeRow && (
                <>
                  {userCCs.length > 1 && <div className="cc-selector-separator" />}
                  <button
                    onClick={() => navigate("/team")}
                    className={`cc-pill cc-pill--team${subTab === "team" ? " cc-pill--active" : ""}`}
                  >
                    <span className="material-icons" style={{ fontSize: 15, marginRight: 4 }}>groups</span>
                    My Team
                  </button>
                </>
              )}
              {hasLeadershipView && primaryTab === "forecast" && (
                <>
                  <div className="cc-selector-separator" />
                  <button
                    className={`cc-pill cc-pill--team${forecastScope === "team" ? " cc-pill--active" : ""}`}
                    onClick={() => {
                      setForecastScope("team");
                      // Team rollup only exists on the reporting tabs (no
                      // aggregate editing across CCs) — clicking this from
                      // Expense/Salary/Contract/Headcount takes you there
                      // instead of silently doing nothing on a page that
                      // has no team view of its own.
                      if (subTab !== "forecast-comparison" && subTab !== "budget-overview") {
                        navigate("/budget-overview");
                      }
                    }}
                    title="Roll up across every cost center you lead"
                  >
                    <span className="material-icons" style={{ fontSize: 15, marginRight: 4 }}>groups</span>
                    My Team
                  </button>
                </>
              )}
            </div>
          )}
          {!effectiveIsAdmin && !isImpersonating && primaryTab === "actuals" && (
            <div id="page-title-controls-slot" className="cc-content-row-controls" />
          )}
        </div>
      )}

      {/* Forecast sub-tabs + cycle switcher + window status */}
      {!effectiveIsAdmin && primaryTab === "forecast" && (
        <div className="app-sub-nav">
          <div className="app-sub-nav-left">
            <button
              onClick={() => navigate("/expense")}
              className={`nav-tab nav-tab--sub nav-tab--sub-expense${subTab === "expense" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">receipt_long</span>
              Expense
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/salary")}
              className={`nav-tab nav-tab--sub nav-tab--sub-salary${subTab === "salary" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">payments</span>
              Salary &amp; BIP
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/temp-labor")}
              className={`nav-tab nav-tab--sub nav-tab--sub-temp-labor${subTab === "temp-labor" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">engineering</span>
              Contract/Outside Services
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/headcount")}
              className={`nav-tab nav-tab--sub nav-tab--sub-headcount${subTab === "headcount" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">group</span>
              Headcount
            </button>
          </div>
          <div className="app-sub-nav-middle">
            <button
              onClick={() => navigate("/forecast-comparison")}
              className={`nav-tab nav-tab--sub nav-tab--sub-compare${subTab === "forecast-comparison" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">compare_arrows</span>
              Forecast Comparison
            </button>
            <div className="sub-nav-separator" />
            <button
              onClick={() => navigate("/budget-overview")}
              className={`nav-tab nav-tab--sub nav-tab--sub-budget-overview${subTab === "budget-overview" ? " nav-tab--sub-active" : ""}`}
            >
              <span className="material-icons">account_balance_wallet</span>
              Budget Overview
            </button>
          </div>
          <div className="app-sub-nav-right">
            <div id="forecast-cycle-slot" />
          </div>
        </div>
      )}

      <div style={{ padding: "0 24px" }}>
        {!effectiveIsAdmin && (subTab === "salary" || subTab === "temp-labor") && effectiveCC && (
          <YTGSimulationStrip key={simRefreshKey} costCenterNumber={effectiveCC.cost_center_number} />
        )}
        <Outlet />
      </div>
    </div>
  );

  if (popupAuthPending) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        Completing sign in...
      </div>
    );
  }

  return (
    <div className="app-container">
      <Routes>
        <Route path="/login" element={<LoginView onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/callback" element={<EntraCallback onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/change-password" element={<ChangePasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Admin views */}
          <Route path="/admin" element={<Navigate to="/admin/bu-dashboard" replace />} />
          <Route path="/admin/:tab" element={<AdminPanel onImpersonate={handleStartImpersonation} isSuperAdmin={isSuperAdmin} />} />

          {/* Actuals > My Team (Actuals Dashboard scoped to user's BUs/CCs) */}
          <Route
            path="/team"
            element={
              authenticatedUser && (hasLeadershipView || effectiveIsAdmin) && !isImpersonating
                ? <ActualsDashboard apiPrefix="/team" fiscalYear={activeFY || fyLabel} />
                : <Navigate to="/dashboard" replace />
            }
          />

          {/* Actuals > My Cost Centers */}
          <Route
            path="/dashboard"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <Dashboard costCenterNumber={effectiveCC!.cost_center_number} costCenterName={effectiveCC!.cost_center_name} />
                : null
            }
          />

          {/* Forecast > Expense */}
          <Route
            path="/expense"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <BudgetEditor costCenterNumber={effectiveCC!.cost_center_number} costCenterName={effectiveCC!.cost_center_name} costCenterOwner={effectiveCC!.cost_center_owner} fiscalYear={fyLabel} isAdmin={effectiveIsAdmin} canAlwaysEdit={isAdmin} />
                : null
            }
          />
          <Route
            path="/headcount"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <HeadcountEditor costCenterNumber={effectiveCC!.cost_center_number} isAdmin={effectiveIsAdmin} />
                : null
            }
          />
          <Route
            path="/salary"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <SalaryView costCenterNumber={effectiveCC!.cost_center_number} isAdmin={effectiveIsAdmin} onDataSaved={bumpSimulation} />
                : null
            }
          />
          <Route path="/bip" element={<Navigate to="/salary" replace />} />
          <Route
            path="/temp-labor"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <TempLaborView costCenterNumber={effectiveCC!.cost_center_number} onDataSaved={bumpSimulation} />
                : null
            }
          />
          <Route
            path="/forecast-comparison"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <BudgetCompareView costCenterNumber={effectiveCC!.cost_center_number} costCenterName={effectiveCC!.cost_center_name} scope={forecastScope} />
                : null
            }
          />
          <Route
            path="/budget-overview"
            element={
              authenticatedUser && effectiveHasOwnCC
                ? <BudgetOverviewView costCenterNumber={effectiveCC!.cost_center_number} costCenterName={effectiveCC!.cost_center_name} scope={forecastScope} />
                : null
            }
          />

          {/* Root redirect */}
          <Route path="/" element={
            <Navigate to={
              effectiveIsAdmin ? "/admin/forecast-windows"
              : effectiveHasOwnCC ? "/dashboard"
              : hasLeadershipView ? "/team"
              : "/dashboard"
            } replace />
          } />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => (
  <MsalProvider instance={msalInstance}>
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </MsalProvider>
);

export default App;
